import { withBasePath } from '@src/common/path-convert';
import { isiOSShare } from '@src/utils/util';
import { getYear } from 'date-fns';
import { useTranslation } from 'next-i18next';
import useWindowSize from '@src/utils/useWindowSize';
import cn from 'classnames';
import { nonPageTracker } from '@src/adobe-launch/data-layer-provider';
import { TrackingEvent } from '@src/adobe-launch/types';
import { ContentBlockFragment, KolProfileFragment } from '@graphqlTypes/sitecore';
import { useEffect, useRef } from 'react';
import { debounce } from 'lodash';

interface Props {
    currentPath: string;
    contentBlock?: ContentBlockFragment | KolProfileFragment;
    getOverLayHeight: (v: number) => void;
}

const Open3in1App = (props: Props) => {
    const { currentPath, contentBlock, getOverLayHeight } = props;
    const overLayRef = useRef<HTMLDivElement | null>(null);
    const { t, i18n } = useTranslation('share');
    const { width } = useWindowSize();

    const title = contentBlock?.pageTitle?.value || '';
    const id = contentBlock?.id || '';
    const isEn = i18n.language.toLowerCase() === 'en-us';
    const overW834 = width >= 834;
    const isWidth1440 = width >= 1440;
    const waLang =
        i18n.language.toLocaleLowerCase() === 'en-us' ? 'en' : i18n.language.toLocaleLowerCase();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const GetPath: any = {
        en_US_ios: 'https://apps.apple.com/hk/app/id432462569?l=en',
        zh_HK_ios: 'https://apps.apple.com/hk/app/id432462569?l=zh-hk',
        en_US_andriod: 'https://m.hkjc.com/en/download-classic-3-in-1.html',
        zh_HK_andriod: 'https://m.hkjc.com/tc/download-classic-3-in-1.html',
    };

    const openShareUrl = debounce(
        () => {
            nonPageTracker(
                {
                    eventID: TrackingEvent.Open3in1,
                    title,
                    id,
                    language: waLang,
                },
                undefined,
                true
            );
            window.open(`hkjcbet://discover/${currentPath}`, `_self`);
        },
        500,
        { leading: true, trailing: false }
    );

    const GoDownload = debounce(
        () => {
            nonPageTracker(
                {
                    eventID: TrackingEvent.Download3in1,
                    title,
                    id,
                    language: waLang,
                },
                undefined,
                true
            );
            const b = i18n.language.split('-').join('_');
            if (isiOSShare) {
                window.location.href = GetPath[`${b}_ios`];
            } else {
                window.location.href = GetPath[`${b}_andriod`];
            }
        },
        500,
        { leading: true, trailing: false }
    );

    // 更新覆盖层高度
    useEffect(() => {
        if (overLayRef.current) {
            const height = overLayRef.current.offsetHeight;
            getOverLayHeight(height);
        }
    }, [getOverLayHeight]);

    useEffect(() => {
        return () => {
            openShareUrl.cancel();
            GoDownload.cancel();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            className={cn(
                'fixed -bottom-1 left-0 right-0 pb-4 pt-3 transition-transform duration-400 ease-in-out bg-white z-50 pointer-events-auto'
            )}
            style={{
                maxWidth: '1440px',
                margin: '0 auto',
            }}
            ref={overLayRef}
        >
            <div className={cn('relative w-full', isWidth1440 && '!px-[46px]')}>
                <div
                    style={{
                        position: 'absolute',
                        top: '-85px',
                        left: '0',
                        width: '100%',
                        height: '80px',
                        background:
                            'linear-gradient(180deg, rgba(255, 255, 255, 0) 4.94%, #FFFFFF 71.6%)',
                    }}
                ></div>
                <div className='px-4'>
                    <div className='flex pb-3'>
                        <img
                            className='w-[55px] h-[55px]'
                            src={withBasePath('/images/sharePage/i-hkjc.png')}
                            loading='lazy'
                            alt=''
                        />
                        <div className='flex pl-3 flex-col'>
                            <h3 className='text-lg md:text-xl font-bold'>{t('share:title')}</h3>
                            <p className='text-sm md:text-[14px] font-medium leading-[16px]'>
                                {t('share:secondTitle')}
                            </p>
                        </div>
                    </div>
                    {overW834 ? (
                        <div className='flex flex-col'>
                            <div className='flex gap-4'>
                                <div
                                    className='w-full h-10 leading-10 text-center rounded-full bg-[#003C84] text-lg text-white font-medium cursor-pointer'
                                    onClick={openShareUrl}
                                >
                                    {t('share:btnOpen')}
                                </div>
                                <div
                                    className='w-full h-10 leading-10 text-center rounded-full border border-[#003C84] text-lg text-[#003C84] font-medium cursor-pointer'
                                    onClick={GoDownload}
                                >
                                    {t('share:downLoad')}
                                </div>
                            </div>
                            <div className='pt-[8px] text-xs font-normal'>{t('share:notice')}</div>
                        </div>
                    ) : (
                        <>
                            <div className='flex gap-2'>
                                <div
                                    className='w-full h-10 leading-10 text-center rounded-full bg-[#003C84] text-[16px] ss:text-[13px] text-white font-medium cursor-pointer'
                                    onClick={openShareUrl}
                                >
                                    {t('share:btnOpen')}
                                </div>
                                <div
                                    className='w-full h-10 leading-10 text-center rounded-full border border-[#003C84] text-[16px] ss:text-[13px] text-[#003C84] font-medium cursor-pointer'
                                    onClick={GoDownload}
                                >
                                    {t('share:downLoad')}
                                </div>
                            </div>

                            <div className='pt-[8px] pb-3 text-[12px]'>{t('share:notice')}</div>
                        </>
                    )}

                    <div className=' text-[12px]'>
                        <>
                            <div className='font-bold text-[9px] pb-1'>
                                {isEn ? 'Play Responsibly' : '博彩要有節制'}
                            </div>
                            <div className='text-[9px] font-medium text-black leading-[10px]'>
                                {t('share:ruleText')}
                            </div>
                            <div className='text-[9px] text-[#555555] font-medium pt-2'>
                                {isEn
                                    ? `Copyright © 2000-${getYear(
                                          new Date()
                                      )} The Hong Kong Jockey Club`
                                    : `版權所有 不得轉載 © 2000-${getYear(new Date())} 香港賽馬會`}
                            </div>
                        </>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Open3in1App;
